import type { KeyboardEvent } from 'react';

import { Button } from '../../components/Button';
import { Carousel } from '../../components/Carousel';
import { EmptyMessage } from '../../components/EmptyMessage';
import { PageHeading } from '../../components/PageHeading';
import { PageSection } from '../../components/Section';
import { SkeletonBlock } from '../../components/SkeletonBlock';
import { SkeletonCard, SkeletonRow } from '../../components/SkeletonCard';
import { SpaItemCard } from '../../composites/SpaItemCard';
import { ChevronRightIcon } from '../../icons/ChevronRightIcon';
import {
  BannerCard,
  BannerImage,
  BannerOverlay,
  BannerPlaceholder,
  BannerTitle,
  SectionBlock,
  SectionTitle,
  SectionTitleGroup,
} from './SpaLandingTemplate.styles';
import type { SpaLandingSection, SpaLandingTemplateProps } from './SpaLandingTemplate.types';

function SectionTitleRow({ title, onTitleClick }: Pick<SpaLandingSection, 'title' | 'onTitleClick'>) {
  return (
    <SectionTitleGroup>
      <SectionTitle role="heading" aria-level={2}>{title}</SectionTitle>
      <Button variant="icon" onClick={onTitleClick} aria-label={`View all ${title}`}>
        <ChevronRightIcon size={20} />
      </Button>
    </SectionTitleGroup>
  );
}

export function SpaLandingTemplate({
  heading,
  sections,
  classesBanner,
  isLoading = false,
  fromPriceLabel,
  soldOutLabel,
  noImageLabel,
  noResultsLabel,
}: SpaLandingTemplateProps) {
  if (isLoading) {
    return (
      <PageSection data-testid="spa-landing-loading">
        <PageHeading title={heading} />
        {Array.from({ length: 3 }).map((_, i) => (
          <SectionBlock key={i}>
            <SkeletonBlock width="120px" height="24px" />
            <SkeletonRow>
              {Array.from({ length: 3 }).map((_, j) => (
                <SkeletonCard key={j} variant="grid" />
              ))}
            </SkeletonRow>
          </SectionBlock>
        ))}
        <SectionBlock>
          <SkeletonBlock width="100%" height="200px" borderRadius="8px" />
        </SectionBlock>
      </PageSection>
    );
  }

  if (sections.length === 0 && !classesBanner) {
    return (
      <PageSection data-testid="spa-landing-empty">
        <PageHeading title={heading} />
        <EmptyMessage>{noResultsLabel ?? 'No results available.'}</EmptyMessage>
      </PageSection>
    );
  }

  const handleBannerKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      classesBanner?.onSelect();
    }
  };

  return (
    <PageSection data-testid="spa-landing-template">
      <PageHeading title={heading} />

      {sections.map((section) => (
        <SectionBlock key={section.id} data-testid={`section-${section.id}`}>
          <Carousel
            title={<SectionTitleRow title={section.title} onTitleClick={section.onTitleClick} />}
            visibleCount={{ mobile: 1.2, desktop: 3 }}
            showProgress
          >
            {section.items.map((item) => (
              <SpaItemCard
                key={item.id}
                id={item.id}
                name={item.name}
                imageUrl={item.imageUrl}
                imageAspectRatio="1 / 1"
                price={item.price}
                discount={item.discount}
                duration={item.duration}
                unavailable={item.unavailable}
                promotionName={item.promotionName}
                fromPriceLabel={fromPriceLabel}
                soldOutLabel={soldOutLabel}
                noImageLabel={noImageLabel}
                onSelect={section.onSelectItem}
              />
            ))}
          </Carousel>
        </SectionBlock>
      ))}

      {classesBanner && (
        <SectionBlock data-testid="section-classes">
          <BannerCard
            role="button"
            tabIndex={0}
            onClick={classesBanner.onSelect}
            onKeyDown={handleBannerKeyDown}
            aria-label={classesBanner.title}
          >
            {classesBanner.imageUrl ? (
              <BannerImage src={classesBanner.imageUrl} alt={classesBanner.title} />
            ) : (
              <BannerPlaceholder>{noImageLabel ?? 'No image'}</BannerPlaceholder>
            )}
            <BannerOverlay>
              <BannerTitle>{classesBanner.title}</BannerTitle>
              <Button variant="secondary" size="small" style={{ alignSelf: 'flex-start' }}>{classesBanner.buttonLabel}</Button>
            </BannerOverlay>
          </BannerCard>
        </SectionBlock>
      )}
    </PageSection>
  );
}
